<template>
    <v-progress-linear indeterminate v-if="loadingProteins"></v-progress-linear>
    <v-form ref="addNewAnalysis" v-model="valid" v-else>
        <v-row>
            <v-col cols="12">
                <v-menu
                    ref="datePickerMenu"
                    :close-on-content-click="false"
                    v-model="datePickerMenu"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px">
                    <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                            clearable
                            v-model="displayDate"
                            label="Date"
                            prepend-icon="event"
                            readonly
                            :rules="[$rules.required]"
                            v-bind="attrs"
                            v-on="on"
                        ></v-text-field>
                    </template>
                    <v-date-picker 
                        v-model="analysis.AnalysisDate"
                        @input="datePickerMenu = false"
                        :title-date-format="datePickerFormat"
                    >
                    </v-date-picker>
                </v-menu>
            </v-col>
            <v-col cols="12">
                <v-simple-table>
                    <template v-slot:default>
                        <tbody>
                            <tr
                                v-for="item in analysis.Proteins"
                                :key="item.VenomAnalysisProteinID"
                            >
                                <td class="width-25percent">
                                    <v-select
                                        :items="proteinItems(item.VenomProteinID)"
                                        label="Protein"
                                        v-model="item.VenomProteinID"
                                        :rules="[$rules.selectIDRequired]">
                                    </v-select>
                                </td>
                                <td class="width-15percent">
                                    <NumericEntry
                                        v-model="item.LowRange"
                                        label="Low Area %"
                                        :rules="[$rules.required]">
                                    </NumericEntry>
                                </td>
                                <td class="width-15percent">
                                    <NumericEntry
                                        v-model="item.HighRange"
                                        label="High Area %"
                                        :rules="[$rules.required]">
                                    </NumericEntry>
                                </td>
                                <td>
                                    <v-textarea label="Comment" 
                                        v-model="item.Comments"
                                    ></v-textarea>
                                </td>
                                <td class="width-5percent">
                                    <v-btn 
                                        small
                                        color="error"
                                        @click="removeProtein(item.VenomAnalysisProteinID)">
                                        <v-icon>
                                            mdi-delete
                                        </v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan="5" class="text-center">
                                    <v-btn 
                                        small
                                        color="success"
                                        @click="addProtein">
                                        <v-icon>
                                            mdi-plus
                                        </v-icon>
                                    </v-btn>
                                </td>
                            </tr>
                        </tfoot>
                    </template>
                </v-simple-table>
            </v-col>
            <v-col cols="12">
                <v-textarea v-model="analysis.OtherComments" label="Other Comments"></v-textarea>
            </v-col>
            <v-col cols="12" class="text-center">
                <UploadButton ref="fileUpload" :allowedFileExtensions="allowedFileExtensions" @fileSelected="fileSelected" @fileInvalid="fileInvalid" :maxFileSizeMB="2" />
            </v-col>
            <v-col cols="12" class="text-center" v-if="fileError">
                <v-alert text type="error">{{fileError}}</v-alert>
            </v-col>
            <v-col cols="12" class="text-center">
                <v-btn color="success" @click="addAnalysis" :disabled="!validComplete || saving" :loading="saving">Add Analysis</v-btn>
            </v-col>
            <v-col cols="12" class="text-center" v-if="saveError">
                <v-alert text type="error">{{saveError}}</v-alert>
            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import * as venomAnalysisAPI from '@/services/venomAnalysisAPI';
import moment from 'moment';
import constants from 'comunet-btg-utah-repository-shared/constants';
import NumericEntry from "@/components/common/NumericEntry";
import UploadButton from '@/components/common/UploadButton.vue';
import fileService from '../../services/fileService';

export default {
    components: {
        NumericEntry,
        UploadButton
    },
    props: {
        specimenID: {
            type: Number,
            default: -1
        },
        dailyExtractionID: {
            type: Number,
            default: -1
        },
        monthlyPoolID: {
            type: Number,
            default: -1
        },
        venomLotID: {
            type: Number,
            default: -1
        },
        speciesBatchPoolID: {
            type: Number,
            default: -1
        }
    },
    data() {
        return {
            datePickerMenu: false,
            loadingProteins: false,
            proteins: [],
            loadingError: "",
            analysis: {
            },
            valid: false,
            allowedFileExtensions: '.pdf, .docx, .doc',
            fileError: "",
            pendingFile: null,
            saveError: "",
            saving: false
        };
    },
    computed: {
        displayDate: {
            get: function() {
                if (this.analysis && this.analysis.AnalysisDate) {
                    return moment.utc(this.analysis.AnalysisDate).format(constants.DATE_FORMAT_NO_TIME);
                }
                
                return "";
            },
            set: function(newValue) {
                this.analysis.AnalysisDate = newValue;
            }
        },
        validComplete() {
            return this.valid && (this.analysis.Proteins.length > 0 || this.analysis.OtherComments);
        }
    },
    methods:{
        createBlankAnalysis() {
            this.analysis = {
                SpecimenID: this.specimenID,
                DailyExtractionID: this.dailyExtractionID,
                MonthlyPoolID: this.monthlyPoolID,
                VenomLotID: this.venomLotID,
                SpeciesBatchPoolID: this.speciesBatchPoolID,
                AnalysisDate: moment().format('YYYY-MM-DD'),
                OtherComments: "",
                Proteins: []
            };

            this.pendingFile = null;

            if (this.$refs.fileUpload) {
                this.$refs.fileUpload.reset();
            }
        },
        addProtein() {
            this.analysis.Proteins.push({
                VenomAnalysisProteinID: Math.floor(Math.random() * Math.floor(-999999)), // Random ID Key value
                VenomProteinID: undefined,
                Comments: "",
                LowRange: undefined,
                HighRange: undefined
            });

            this.$nextTick(() => this.$refs.addNewAnalysis.validate())
        },
        removeProtein(venomAnalysisProteinID) {
            this.analysis.Proteins = this.analysis.Proteins.filter(y => y.VenomAnalysisProteinID != venomAnalysisProteinID);
        },
        async addAnalysis() {
            this.saving = true;
            this.saveError = "";

            try {
                // Add the data to the database
                const response = await venomAnalysisAPI.saveVenomAnalysis(this.analysis);

                // Add the file
                if (this.pendingFile != null) {
                    var document = {
                        VenomAnalysisID: response.VenomAnalysisID,
                        FileName: this.pendingFile.FileName
                    };

                    await fileService.uploadFileAndSaveDocument(document, this.pendingFile.FileName, "venomAnalysis", this.pendingFile.ContentType, this.pendingFile.FileData)
                }

                this.$emit("created");

                // Clear everything
                this.createBlankAnalysis();
            } catch (e) {
                this.saveError = e.message;
            }
            
            this.saving = false;
        },
        datePickerFormat(date) {
            return moment.utc(date).format(constants.DATE_FORMAT_NO_TIME);
        },
        proteinItems(venomProteinID) {
            let itemList = [];
            itemList.push(...this.proteins.filter(x => x.Active).map(y => {
                return {
                    text: y.Name,
                    value: y.VenomProteinID,
                    sortOrder: y.SortOrder
                }
            }));
            return itemList.sort((a, b) => {
                return a.sortOrder - b.sortOrder
            });
        },
        fileSelected(file) {
            this.fileError = '';
            this.pendingFile = file;
        },
        fileInvalid(errorMessage) {
            this.fileError = errorMessage;
            this.pendingFile = null;
        },
    },
    async created() {
        this.loadingProteins = true;

        try {
            this.proteins = await venomAnalysisAPI.getVenomProteins();
        } catch (e) {
            this.loadingError = e.message
        }

        this.createBlankAnalysis();

        this.loadingProteins = false;
    }
}
</script>

<style scoped>
    td.width-25percent {
        width: 25%;
    }
    td.width-15percent {
        width: 15%;
    }
    td.width-5percent {
        width: 5%;
    }
</style>